import api from "./api";
import {
  ok, getMe, listCyclesCached, cycleById, isStageOpen, findRecord, listRecords, recordDetail, recordSection,
  recordSections, splitItems, employeeFromRecord, getLookupsCached, sheetStatusOf, selfStatusOf, managerStatusOf,
  resultStatusOf, ratingConfigFor, estimatePerformance, performanceFromResult, performanceFromPreview, readSections,
  SELF_SECTION_MAP, MANAGER_SECTION_MAP, isoUtc, nameOf, rememberName, pMapSettled, toTimeline,
  RESULT_STATES, reasonOf, extensionsFromHistory, stripExtensionTag,
  publicConfig,
} from "./core";

const knownNameOr = (id, fallback) => {
  const name = nameOf(id, fallback);
  return name === String(id) ? fallback : name;
};

export async function getMyResults() {
  const cycles = await listCyclesCached();
  const { data: mine } = await api.get("/dashboard", { params: { view: "employee" } });
  const rows = await pMapSettled(mine, async (summary) => {
    const cycle = cycles.find((c) => c.cycleId === summary.cycle_id);
    const config = await ratingConfigFor(cycle);
    const [record, results] = await Promise.all([
      recordDetail(summary.performance_record_id),
      recordSection(summary.performance_record_id, "results", []),
    ]);
    const released = results.find((r) => r.released_at);
    const { goals } = splitItems(record);
    const feedback = released?.manager_feedback || {};
    return {
      recordId: record.performance_record_id,
      cycle: cycle ? { id: cycle.id, name: cycle.name, startDate: cycle.startDate, endDate: cycle.endDate } : { id: String(summary.cycle_id), name: `Cycle ${summary.cycle_id}` },
      draft: cycle?.backendStatus === "DRAFT",
      status: released ? "Finalized" : resultStatusOf(record) === "Finalized" ? "Pending HR Review" : resultStatusOf(record),
      finalizedAt: released ? isoUtc(released.finalized_at || released.released_at) : null,
      releasedAt: released ? isoUtc(released.released_at) : null,
      revisionId: released?.result_revision_id || null,
      acknowledged: record.record_status === "ACKNOWLEDGED",
      acknowledgedAt: isoUtc(record.acknowledged_at),
      acknowledgmentRequired: Boolean(cycle?.workflow?.acknowledgment_required),
      performance: released ? performanceFromResult(released, config.bands) : null,
      managerFeedback: released && Object.keys(feedback).length
        ? { managerName: knownNameOr(record.review_manager_employee_id, "your manager"), ...readSections(feedback, MANAGER_SECTION_MAP) }
        : null,
      goals,
      config: publicConfig(config),
    };
  }, 4, null);
  return ok(rows.filter((r) => r && !r.draft));
}

export const acknowledgeResult = (revisionId, reason = "Result acknowledged") =>
  api.post(`/results/${revisionId}/acknowledge`, { reason });

function reviewRow(summary, reportRow, lookups, perf) {
  const snapshot = {
    employee_id: summary.employee_id,
    employee_name: reportRow?.employee_name,
    department_id: reportRow?.department_id,
    department_name: reportRow?.department_name,
    designation_name: reportRow?.designation_name,
  };
  rememberName(summary.employee_id, reportRow?.employee_name);
  const record = { ...summary, context_snapshot: snapshot };
  return {
    recordId: summary.performance_record_id,
    employee: { ...employeeFromRecord(record, lookups), status: "Active" },
    sheetStatus: sheetStatusOf(summary),
    selfStatus: selfStatusOf(summary),
    managerStatus: managerStatusOf(summary),
    resultStatus: resultStatusOf(summary),
    eligibility: summary.eligibility_status,
    stage: summary.current_stage,
    performance: perf,
  };
}

export async function getPerformanceResults(cycleId, filters = {}) {
  const [lookups, cycle] = await Promise.all([getLookupsCached(), cycleById(cycleId)]);
  const config = await ratingConfigFor(cycle);
  const [records, report] = await Promise.all([
    listRecords(cycleId),
    api.get("/reports/employee-performance", { params: { cycle_id: cycleId, department_id: filters.departmentId || undefined, limit: 500 } }).then((r) => r.data.rows),
  ]);
  const byEmployee = Object.fromEntries(report.map((r) => [r.employee_id, r]));
  const summaries = records.filter((r) => byEmployee[r.employee_id]);
  const rows = await pMapSettled(summaries, async (summary) => {
    const status = resultStatusOf(summary);
    let perf = { goalScore: null, kraScore: null, kpiScore: null, finalScore: null, rating: "Not Yet Rated", ratingTone: "neutral", status };
    if (status === "Finalized") {
      const results = await recordSection(summary.performance_record_id, "results", []);
      if (results[0]) perf = performanceFromResult(results[0], config.bands, status);
    } else if (status === "Pending HR Review") {
      const preview = await recordSection(summary.performance_record_id, "score_preview", null);
      if (preview) perf = performanceFromPreview(preview, config.bands, status);
    }
    return reviewRow(summary, byEmployee[summary.employee_id], lookups, perf);
  }, 6, null);
  const q = (filters.search || "").trim().toLowerCase();
  return ok(rows.filter(Boolean)
    .filter((r) => !filters.status || r.resultStatus === filters.status)
    .filter((r) => !q || r.employee.name.toLowerCase().includes(q) || r.employee.id.toLowerCase().includes(q)));
}

export async function getPerformanceReview(cycleId, employeeId) {
  const summary = await findRecord(cycleId, employeeId);
  const rid = summary.performance_record_id;
  const [record, lookups, me] = await Promise.all([recordDetail(rid), getLookupsCached(), getMe()]);
  const cycle = await cycleById(record.cycle_id);
  const config = await ratingConfigFor(cycle);
  const { goals, kras, kpis } = splitItems(record);
  const resultStatus = resultStatusOf(record);

  const wanted = ["results", "exceptions", "review_readiness", "history", "change_requests"];
  if (record.manager_submitted_at && !record.current_result_revision_id) wanted.push("score_preview");
  const sections = {};
  await Promise.all(wanted.map(async (name) => {
    try {
      Object.assign(sections, await recordSections(rid, [name]));
    } catch {
      sections[name] = null;
    }
  }));

  const results = sections.results || [];
  const current = results.find((r) => r.result_revision_id === record.current_result_revision_id) || results[0] || null;
  let performance = estimatePerformance({ goals, kras, kpis }, config, resultStatus);
  if (current) performance = performanceFromResult(current, config.bands, resultStatus);
  else if (sections.score_preview) performance = performanceFromPreview(sections.score_preview, config.bands, resultStatus);

  const managerName = nameOf(record.review_manager_employee_id);
  const assessment = (kind) => {
    const raw = kind === "self" ? record.self_assessment : record.manager_assessment;
    const submittedAt = kind === "self" ? record.self_submitted_at : record.manager_submitted_at;
    if (!raw || (!Object.keys(raw.sections || {}).length && !(raw.items || []).length && !submittedAt)) return null;
    return {
      ...readSections(raw.sections, kind === "self" ? SELF_SECTION_MAP : MANAGER_SECTION_MAP),
      status: kind === "self" ? selfStatusOf(record) : managerStatusOf(record),
      submittedAt: isoUtc(submittedAt),
      managerName,
    };
  };

  return ok({
    recordId: rid,
    record,
    cycle: cycle || { id: String(record.cycle_id), name: `Cycle ${record.cycle_id}` },
    employee: employeeFromRecord(record, lookups),
    goals,
    kras,
    kpis,
    selfAssessment: assessment("self"),
    managerAssessment: assessment("manager"),
    result: {
      status: resultStatus,
      revisionId: record.current_result_revision_id,
      finalizedAt: isoUtc(record.finalized_at || current?.finalized_at),
      finalizedByName: nameOf(record.finalized_by_employee_id || current?.finalized_by_employee_id),
      hrComments: current?.correction_reason || "",
      released: ["RELEASED", "ACKNOWLEDGED"].includes(record.record_status),
      releasedAt: isoUtc(record.released_at),
      acknowledgedAt: isoUtc(record.acknowledged_at),
      revisions: results.map((r) => ({
        id: r.result_revision_id,
        revisionNo: r.revision_no,
        finalScore: r.final_score,
        rating: r.rating_label_snapshot,
        releasedAt: isoUtc(r.released_at),
        finalizedAt: isoUtc(r.finalized_at),
        correctionReason: stripExtensionTag(r.correction_reason),
      })),
    },
    performance,
    config: publicConfig(config),
    stageOpen: isStageOpen(cycle, "hrFinalization", extensionsFromHistory(sections.history || [])) && record.current_stage === "HR_FINALIZATION",
    releaseMode: cycle?.workflow?.release_mode || "MANUAL",
    exceptions: (sections.exceptions || []).map((x) => ({
      id: x.exception_id,
      code: x.exception_code,
      severity: x.severity,
      description: x.description,
      status: x.exception_status,
      raisedAt: isoUtc(x.raised_at),
      resolution: x.resolution_reason,
    })),
    readiness: sections.review_readiness,
    history: toTimeline(sections.history || []),
    eligibility: record.eligibility_status,
    reviewerId: record.review_manager_employee_id,
    viewerId: me.employee_id,
    isFinal: Boolean(record.current_result_revision_id) || RESULT_STATES.includes(record.record_status),
  });
}

export async function finalizeResult(cycleId, employeeId, payload = {}) {
  const summary = await findRecord(cycleId, employeeId);
  return api.post(`/records/${summary.performance_record_id}/finalize`, { reason: reasonOf(payload.hrComments, "Finalized by HR") });
}

export async function releaseResult(cycleId, employeeId, reason = "Result released to the employee") {
  const summary = await findRecord(cycleId, employeeId);
  return api.post(`/records/${summary.performance_record_id}/release`, { reason });
}

export async function reopenResult(cycleId, employeeId, { reason, correction } = {}) {
  const summary = await findRecord(cycleId, employeeId);
  const record = await recordDetail(summary.performance_record_id);
  const manager = correction || record.manager_assessment || {};
  const text = String(reason || "").trim();
  if (!text) throw new Error("Please write why the result is being corrected.");
  return api.post(`/records/${record.performance_record_id}/corrections`, {
    sections: manager.sections || {},
    items: (manager.items || []).map((x) => ({ item_id: Number(x.item_id), achievement: Number(x.achievement), comment: x.comment || "" })),
    reason: reasonOf(text, "Result corrected"),
  });
}
